// Rate limiting: the COUNTING, and deliberately not the POSTURE.
//
// Three call sites shared two algorithms and agreed on nothing else. Measured on
// this checkout (2026-09-04), before this module existed:
//
//   apps/api/src/middleware/auth.ts  in-memory sliding window, UNBOUNDED map
//   apps/api/src/middleware/auth.ts  Redis sliding window (ZSET, Lua)
//   apps/ingest/src/rate-limit.ts:30      in-memory fixed window, one row per source
//   apps/ingest/src/rate-limit.ts:62      Redis fixed window, INCR + PEXPIRE, 2 trips
//   apps/marketing/src/server/rate-limit.ts:69  Redis fixed window (INCR, Lua)
//   apps/marketing/src/server/rate-limit.ts:92  in-memory sliding window, BOUNDED
//
// Plan 101 slice 04 called the api/marketing pair "2 verbatim copies (incl. the
// Lua sliding window)". They are not, and it matters: api counts a SLIDING window
// over a sorted set, marketing counts a FIXED window with `INCR`, the two Lua
// scripts share no line, and the two verdict types answer different questions.
// What IS duplicated is smaller and real — the in-memory sliding window (api ≡
// marketing, marketing's being the bounded one) and the Redis fixed-window
// counter (marketing's Lua ≡ ingest's two round trips). Those are what live here.
//
// ── NOTHING IN THIS FILE CATCHES ──────────────────────────────────────────────
// The three sites hold three DIFFERENT failure postures, and they are three
// security decisions rather than three copies of one:
//
//   apps/api        FAIL CLOSED — a throw leaves the middleware and becomes a 500.
//                   The pre-auth per-IP bucket is the only thing bounding
//                   credential probing, so a limiter that cannot count must not
//                   admit.
//   apps/marketing  FAIL OPEN, logged, at the route (`safeHit`) — a Dragonfly blip
//                   must not block a real visitor from reaching a contact form.
//   apps/ingest     FAIL OPEN, at the limiter — refusing a delivery whose HMAC
//                   already verified loses a customer's signal for good.
//
// A `try`/`catch` here would collapse those into whichever one this file picked,
// and it would do it silently: api would start admitting on a Dragonfly outage
// and no test, no type and no log line would say so. So every function here
// PROPAGATES, `rate-limit.test.ts` pins that it does, and the posture stays a
// visible decision at each seam.

import { type Sleep, sleep } from './time';

/**
 * What one `hit` decided. Reported on EVERY response through `RateLimit-*`
 * headers by callers that publish a budget, not just on the 429 it refuses with.
 */
export type RateLimitVerdict = {
  /** Allowed? `false` ⇒ refused; wait {@link resetSeconds} before retrying. */
  ok: boolean;
  /** Budget left in this window AFTER this request. `0` on a refusal. */
  remaining: number;
  /**
   * Seconds until the window frees a slot — a DELTA, never an epoch. ONE number
   * with two names on the wire (`RateLimit-Reset` always, `Retry-After` on the
   * 429) because for a sliding window they are the same instant: the oldest hit
   * aging out is exactly what admits the next request. Two fields would be two
   * chances to answer "when may I retry?" differently.
   */
  resetSeconds: number;
};

/**
 * Sliding-window limiter. `key` identifies the caller (token id, IP, …);
 * `capacity` is the max allowed in `windowMs`.
 */
export type RateLimiter = {
  hit(key: string, capacity: number, windowMs: number): Promise<RateLimitVerdict>;
};

/**
 * The Redis surface every limiter here needs, and the only one. Structural, so
 * this package still imports nothing: a real ioredis client satisfies it, and a
 * unit test satisfies it with an object literal. Dragonfly supports `EVAL`
 * natively, which is why both scripts below are one round trip rather than a
 * sequence a crash can interrupt half-applied.
 */
export type RedisEvalLike = {
  eval(
    script: string,
    numKeys: number,
    key: string,
    ...args: (string | number)[]
  ): Promise<unknown>;
};

/** Default Redis key namespace. A caller with its own namespace passes one. */
export const RATE_LIMIT_KEY_PREFIX = 'ratelimit:';

/**
 * Hard ceiling on distinct keys held by the in-memory limiter.
 *
 * It is not decoration: the pre-auth bucket keys on the CALLER's IP, so the key
 * space is attacker-chosen and the map is boot-lived — the OOMKill shape
 * `docs/standards/long-running-resources.md` names. Eviction can only ever ADMIT
 * a request that would otherwise have been refused, which is the direction a
 * memory guard must fail in.
 */
export const DEFAULT_MAX_KEYS = 10_000;

/**
 * Process-local sliding window. Not shared across replicas — use
 * {@link createRedisRateLimiter} anywhere more than one process serves the
 * budget. Inject `now` for a deterministic clock in tests.
 */
export function createInMemoryRateLimiter(
  now: () => number = () => Date.now(),
  maxKeys = DEFAULT_MAX_KEYS,
): RateLimiter {
  // Each bucket carries the window it was last counted over, so the sweep
  // prunes it by ITS OWN window. Pruning every bucket by the triggering call's
  // window let a 10 s caller wipe a shared limiter's 1 h history.
  const hits = new Map<string, { windowMs: number; list: number[] }>();
  let lastPrune = 0;

  // Without this, a key's bucket lives forever unless that same key is hit
  // again — so a caller rotating keys grows the Map unbounded. Sweep expired
  // buckets at most once per triggering window, and cap total keys (evicting
  // the oldest, i.e. least-recently-inserted) as a hard ceiling.
  const pruneExpired = (t: number, windowMs: number): void => {
    if (t - lastPrune < windowMs) return;
    lastPrune = t;
    for (const [bucketKey, bucket] of hits) {
      const cutoff = t - bucket.windowMs;
      const recent = bucket.list.filter((x) => x > cutoff);
      if (recent.length === 0) hits.delete(bucketKey);
      else bucket.list = recent;
    }
  };

  return {
    hit: async (key, capacity, windowMs) => {
      const t = now();
      pruneExpired(t, windowMs);
      if (!hits.has(key) && hits.size >= maxKeys) {
        const oldestKey = hits.keys().next().value;
        if (typeof oldestKey === 'string') hits.delete(oldestKey);
      }
      const cutoff = t - windowMs;
      const list = (hits.get(key)?.list ?? []).filter((x) => x > cutoff);
      // Seconds until the OLDEST live hit ages out — the instant a slot frees.
      // An empty bucket has no such hit, so the answer is the one we are about
      // to add: a full window away.
      const oldest = list[0] ?? t;
      const resetSeconds = Math.max(1, Math.ceil((oldest + windowMs - t) / 1000));
      if (list.length >= capacity) {
        hits.set(key, { windowMs, list });
        return { ok: false, remaining: 0, resetSeconds };
      }
      list.push(t);
      hits.set(key, { windowMs, list });
      return { ok: true, remaining: capacity - list.length, resetSeconds };
    },
  };
}

/**
 * Atomic sliding-window check-and-add. Same semantics as
 * {@link createInMemoryRateLimiter} (one member per hit, scored by timestamp;
 * deny once `capacity` members fall inside `window`), but server-side under a
 * single round trip so concurrent replicas share one fair window instead of each
 * keeping a process-local count.
 *
 *   KEYS[1] = bucket key
 *   ARGV[1] = now (ms)  ARGV[2] = window (ms)
 *   ARGV[3] = capacity  ARGV[4] = unique member
 *   → {allowed, remaining, resetSeconds}
 *
 * `reset` is computed BEFORE the ZADD and on both branches, so the allowed and
 * refused paths answer the same question with the same arithmetic: seconds until
 * the oldest live hit ages out. With an empty bucket there is no such hit, so it
 * is the one this call adds — a full window away.
 */
export const SLIDING_WINDOW_LUA = `
local now = tonumber(ARGV[1])
local window = tonumber(ARGV[2])
local capacity = tonumber(ARGV[3])
redis.call('ZREMRANGEBYSCORE', KEYS[1], '-inf', now - window)
local count = redis.call('ZCARD', KEYS[1])
local oldest = redis.call('ZRANGE', KEYS[1], 0, 0, 'WITHSCORES')
local reset = window
if oldest[2] then reset = tonumber(oldest[2]) + window - now end
reset = math.ceil(reset / 1000)
if reset < 1 then reset = 1 end
if count >= capacity then
  return {0, 0, reset}
end
redis.call('ZADD', KEYS[1], now, ARGV[4])
redis.call('PEXPIRE', KEYS[1], window)
return {1, capacity - count - 1, reset}
`;

/**
 * Redis/Dragonfly-backed sliding-window limiter. Shared across replicas and
 * durable across restarts — the production default. Keep
 * {@link createInMemoryRateLimiter} for tests and local single-process runs only.
 *
 * THROWS on a client failure; see this file's header on why it must.
 */
export function createRedisRateLimiter(
  redis: RedisEvalLike,
  opts: { keyPrefix?: string; now?: () => number } = {},
): RateLimiter {
  const prefix = opts.keyPrefix ?? RATE_LIMIT_KEY_PREFIX;
  const now = opts.now ?? (() => Date.now());
  return {
    hit: async (key, capacity, windowMs) => {
      const t = now();
      const member = `${t}-${crypto.randomUUID()}`;
      const res = (await redis.eval(
        SLIDING_WINDOW_LUA,
        1,
        `${prefix}${key}`,
        t,
        windowMs,
        capacity,
        member,
      )) as [number, number, number];
      const resetSeconds = Math.max(1, Number(res?.[2]) || 1);
      if (Number(res?.[0]) === 1) {
        return { ok: true, remaining: Math.max(0, Number(res?.[1]) || 0), resetSeconds };
      }
      return { ok: false, remaining: 0, resetSeconds };
    },
  };
}

// ─── fixed window ────────────────────────────────────────────────────────────

/**
 * Fixed-window counter in a single atomic round trip: INCR the counter, set its
 * TTL on the first hit, return the count + remaining TTL. Atomic so a crash
 * between the two commands can't leave a counter without an expiry — which, on a
 * key that outlives its window, is a bucket that blocks its caller forever.
 *
 *   KEYS[1] = bucket key   ARGV[1] = window (ms)   → {count, pttlMs}
 */
export const FIXED_WINDOW_LUA = `
local n = redis.call('INCR', KEYS[1])
if n == 1 then redis.call('PEXPIRE', KEYS[1], tonumber(ARGV[1])) end
return {n, redis.call('PTTL', KEYS[1])}
`;

/** One fixed-window bucket's state after this hit was counted. */
export type FixedWindowCount = {
  /** Hits inside the live window, INCLUDING this one. */
  count: number;
  /** Milliseconds left in the window; the full window when Redis has no usable TTL. */
  ttlMs: number;
};

/**
 * Charge one hit against `key`'s fixed window. The CALLER compares `count`
 * against its own limit and decides what a refusal looks like — a fixed window
 * is spent by budgets that answer in three different shapes (a boolean, a
 * `Retry-After`, a `RateLimit-*` triple), and folding the comparison in here
 * would make this function the author of all three.
 *
 * `key` is passed WHOLE, prefix included: a fixed window may be spelled with the
 * window index in the key (`hookrate:<id>:<floor(now/window)>`, epoch-aligned) or
 * with a stable key whose expiry anchors at the first hit. Both are correct and
 * they are not interchangeable, so the key stays the caller's to choose.
 *
 * THROWS on a client failure; see this file's header on why it must.
 */
export async function chargeFixedWindow(
  redis: RedisEvalLike,
  key: string,
  windowMs: number,
): Promise<FixedWindowCount> {
  const res = (await redis.eval(FIXED_WINDOW_LUA, 1, key, windowMs)) as [number, number];
  // `PTTL` answers -1 (a key with no expiry) and -2 (no key at all), and a
  // garbled reply answers NaN. None of those is "milliseconds left", so they all
  // resolve to the full window rather than riding out as a `Retry-After` of 1
  // second on a bucket that will never free. `FIXED_WINDOW_LUA` cannot produce
  // them — it PEXPIREs on the first INCR, inside the same script — so this
  // covers a key some other writer created, and keeps the field's name true for
  // the next caller.
  const ttl = Number(res?.[1] ?? windowMs);
  return {
    count: Number(res?.[0] ?? 0),
    ttlMs: Number.isFinite(ttl) && ttl > 0 ? ttl : windowMs,
  };
}

// ─── pacing ──────────────────────────────────────────────────────────────────

/** A sliding-window budget: at most `capacity` hits in any `windowMs`. */
export type RateLimitBudget = { capacity: number; windowMs: number };

/** What one paced acquisition cost. */
export type RateLimitWait = {
  /** Total time slept before the slot was granted; `0` when admitted at once. */
  waitedMs: number;
  /** How many hits the limiter refused before admitting this one. */
  refusals: number;
};

/**
 * PACE through `limiter`: hit `key`, and on a refusal sleep the verdict's
 * `resetSeconds` and hit again, until admitted (deep sweep 03-3). The refusing
 * posture's twin, for a sender that must go slower rather than drop: our own SMTP
 * server answers `452 4.4.5` past a send rate, and a 452 burns a retry rung.
 *
 * Built ON the sliding window above, not beside it: with
 * {@link createRedisRateLimiter} every replica draws on one budget, which is the
 * point when the server's limit is per sender, not per pod. A refused hit adds no
 * member to the window in either backend, so waiting does not spend budget.
 *
 * Catches nothing, like the rest of this file: a limiter fault and an abort both
 * propagate, and the caller decides whether that fails the send. The wait is not
 * capped: the window frees a slot within `windowMs` of the oldest hit, and a
 * process that must stop waiting passes `signal`.
 */
export async function waitForRateLimitSlot(
  limiter: RateLimiter,
  key: string,
  budget: RateLimitBudget,
  opts: { sleep?: Sleep; signal?: AbortSignal } = {},
): Promise<RateLimitWait> {
  const { capacity, windowMs } = budget;
  if (!Number.isSafeInteger(capacity) || capacity < 1) {
    throw new Error(
      `waitForRateLimitSlot("${key}"): capacity must be an integer >= 1, got ${capacity} — a budget of 0 never admits, so the wait would never end`,
    );
  }
  if (!Number.isFinite(windowMs) || windowMs <= 0) {
    throw new Error(
      `waitForRateLimitSlot("${key}"): windowMs must be > 0, got ${windowMs} — pass the window the budget is counted over`,
    );
  }
  const pause = opts.sleep ?? sleep;
  let waitedMs = 0;
  let refusals = 0;
  for (;;) {
    const verdict = await limiter.hit(key, capacity, windowMs);
    if (verdict.ok) return { waitedMs, refusals };
    refusals += 1;
    const ms = verdict.resetSeconds * 1_000;
    await pause(ms, opts.signal);
    waitedMs += ms;
  }
}
